import { COMPUTER_TERMINAL_PATH } from "./content-security-policy.js";

export { COMPUTER_TERMINAL_PATH };

/**
 * Browser-to-computer terminal framing. Output from the computer is raw bytes; input is
 * `[kind:u8][length:u32be][payload]` so keystrokes and resizes share one byte stream.
 */
export const TERMINAL_INPUT = 0;
export const TERMINAL_RESIZE = 1;

/** Terminal bytes use the existing sealed control socket, never the RFB screen document. */
export function terminalSocketUrl(session: string, origin: string): string {
  const url = new URL(session, origin);
  if (
    !["http:", "https:"].includes(url.protocol) ||
    url.origin !== new URL(origin).origin ||
    url.username ||
    url.password ||
    !/^\/novnc\/session\/control\/[A-Za-z0-9_.-]+\/(?:embed|vnc|vnc_lite)\.html$/.test(url.pathname)
  )
    throw new Error("Invalid terminal session.");
  url.pathname = url.pathname.replace(/[^/]+$/, "websockify");
  url.search = "";
  url.hash = "";
  url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
  return url.toString();
}

/** Keep the capability in the fragment so the terminal document request never logs it. */
export function computerTerminalDocumentUrl(session: string | null): string | null {
  if (!session) return null;
  try {
    const source = new URL(session);
    terminalSocketUrl(session, source.origin);
    const document = new URL(COMPUTER_TERMINAL_PATH, source.origin);
    document.hash = new URLSearchParams({ session: source.pathname }).toString();
    return document.toString();
  } catch {
    return null;
  }
}

export function encodeTerminalFrame(kind: number, payload: Uint8Array): Uint8Array<ArrayBuffer> {
  const frame = new Uint8Array(5 + payload.length);
  const view = new DataView(frame.buffer);
  view.setUint8(0, kind);
  view.setUint32(1, payload.length);
  frame.set(payload, 5);
  return frame;
}

export function encodeTerminalInput(text: string): Uint8Array<ArrayBuffer> {
  return encodeTerminalFrame(TERMINAL_INPUT, new TextEncoder().encode(text));
}

export function encodeTerminalResize(cols: number, rows: number): Uint8Array<ArrayBuffer> {
  const payload = new Uint8Array(4);
  const view = new DataView(payload.buffer);
  view.setUint16(0, clampSize(cols));
  view.setUint16(2, clampSize(rows));
  return encodeTerminalFrame(TERMINAL_RESIZE, payload);
}

function clampSize(value: number) {
  return Math.min(1000, Math.max(1, Math.round(value) || 1));
}
